import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import StarRatingComponent from 'react-star-rating-component';
import ImageHeader from '../../elements/ImageHeader';
import Text from '../../elements/Text';
import Box from '../../elements/Box';
import Separator from '../_commons/atoms/Separator';
import Icon from '../../elements/Icon';
import FareItemOriginDestination from '../_commons/atoms/FareItemOriginDestination';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import { handleFareItemInteraction } from '../_commons/commonFns';
import { usePlural } from '@front10/utils';

const Price = (props) => {
  const {
    fareItemPricePerNight,
    fareItemPricePerNightContainer,
    fareItemPriceTotal,
    data: { formattedPriceRate: priceRate, formattedTotalPrice: totalPrice, rateType },
    showPricePerNight,
    showPriceTotal,
    separatorStyles,
    perNightStyles,
    priceContainer,
    totalPriceContainer
  } = props;
  const { translate } = useContext(Context);
  return (
    <Box {...priceContainer}>
      {showPricePerNight && priceRate && (
        <Box {...fareItemPricePerNightContainer}>
          <FareItemPrice {...fareItemPricePerNight} formattedTotalPrice={priceRate} />
          <Separator {...separatorStyles} />
          <Text {...perNightStyles}>{translate(`cmp-price-${rateType}-text`, 'Night')}</Text>
        </Box>
      )}
      {showPriceTotal && totalPrice && (
        <Box {...totalPriceContainer}>
          <FareItemPrice {...fareItemPriceTotal} formattedTotalPrice={totalPrice} />
          <Text {...fareItemPriceTotal}>{translate('cmp-price-total-text', 'total')}</Text>
        </Box>
      )}
    </Box>
  );
};

Price.propTypes = {
  fareItemPricePerNight: PropTypes.objectOf(PropTypes.any),
  fareItemPricePerNightContainer: PropTypes.objectOf(PropTypes.any),
  fareItemPriceTotal: PropTypes.objectOf(PropTypes.any),
  showPricePerNight: PropTypes.bool,
  showPriceTotal: PropTypes.bool,
  separatorStyles: PropTypes.objectOf(PropTypes.any),
  priceContainer: PropTypes.objectOf(PropTypes.any),
  perNightStyles: PropTypes.objectOf(PropTypes.any),
  totalPriceContainer: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

Price.defaultProps = {
  fareItemPricePerNight: {},
  fareItemPricePerNightContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  fareItemPriceTotal: {
    textTransform: 'lowercase',
    marginLeft: '1'
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    paddingBottom: '1',
    paddingRight: '1',
    style: { lineHeight: '1' }
  },
  showPricePerNight: true,
  showPriceTotal: true,
  separatorStyles: {
    content: '/',
    paddingLeft: '0',
    paddingRight: '0'
  },
  perNightStyles: {
    textTransform: 'lowercase'
  },
  totalPriceContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  data: {}
};

const Stars = (props) => {
  const {
    reviewItemStyle,
    reviewQtyStyle,
    hostCategoryStyle,
    starRatingStyle,
    iconStyles,
    iconHalfStyles,
    data: { reviewsQty, popularity, travelClass }
  } = props;

  const hostCategory = travelClass; // TODO
  return (
    <Box display="flex" flexDirection="row" {...reviewItemStyle}>
      <StarRatingComponent
        editing={false}
        name="rate1"
        starCount={5}
        value={parseFloat(popularity)}
        renderStarIcon={(index, value) => {
          return (
            <Text {...starRatingStyle}>
              <Icon icon={index <= value ? 'star-full' : 'star-empty'} {...iconStyles} />
            </Text>
          );
        }}
        renderStarIconHalf={() => {
          return (
            <Text {...starRatingStyle}>
              <Icon icon="star-half" {...iconHalfStyles} />
            </Text>
          );
        }}
      />
      <Text {...reviewQtyStyle}>{` ${reviewsQty} `}</Text>
      {hostCategory && (
        <>
          <Text>·</Text>
          <Text {...hostCategoryStyle}>{` ${hostCategory} `}</Text>
        </>
      )}
    </Box>
  );
};

Stars.propTypes = {
  reviewItemStyle: PropTypes.objectOf(PropTypes.any),
  reviewQtyStyle: PropTypes.objectOf(PropTypes.any),
  starRatingStyle: PropTypes.objectOf(PropTypes.any),
  hostCategoryStyle: PropTypes.objectOf(PropTypes.any),
  iconStyles: PropTypes.objectOf(PropTypes.any),
  iconHalfStyles: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

Stars.defaultProps = {
  reviewItemStyle: {},
  reviewQtyStyle: {},
  starRatingStyle: {},
  hostCategoryStyle: {},
  iconStyles: {
    iconColor: 'black',
    size: '10'
  },
  iconHalfStyles: {
    iconColor: 'black',
    size: '10'
  },
  data: {}
};

const Capacity = (props) => {
  const {
    fareItemCapacity,
    data: { beds }
  } = props;
  const translate = usePlural();
  return (
    <Box display="flex" {...fareItemCapacity}>
      <Text floatLeft>{translate({ key: 'cmp-airbnb-item-beds', count: beds })}</Text>
    </Box>
  );
};

Capacity.propTypes = {
  fareItemCapacity: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

Capacity.defaultProps = {
  fareItemCapacity: {},
  data: {}
};

const ItemCard = (props) => {
  const {
    fareItemOriginDestination,
    originDestinationCapacity,
    titlePlaceSettings,
    itemCardContainer,
    separatorStyles,
    data: { placeDescription: titlePlace, originCity: roomType },
    data
  } = props;
  return (
    <>
      <Box rtlReverse {...itemCardContainer}>
        <Box {...originDestinationCapacity}>
          <FareItemOriginDestination
            {...fareItemOriginDestination}
            {...data}
            originCity={roomType}
          />
          {data && data.beds ? (
            <>
              <Separator {...separatorStyles} />
              <Capacity {...props} />
            </>
          ) : null}
        </Box>
        {titlePlace && (
          <Text {...titlePlaceSettings} rtlReverse>
            {titlePlace}
          </Text>
        )}
        <Stars {...props} />
        <Price {...props} />
      </Box>
    </>
  );
};

ItemCard.propTypes = {
  fareItemOriginDestination: PropTypes.objectOf(PropTypes.any),
  originDestinationCapacity: PropTypes.objectOf(PropTypes.any),
  titlePlaceSettings: PropTypes.objectOf(PropTypes.any),
  itemCardContainer: PropTypes.objectOf(PropTypes.any),
  separatorStyles: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

ItemCard.defaultProps = {
  fareItemOriginDestination: {},
  originDestinationCapacity: {},
  titlePlaceSettings: {},
  itemCardContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  separatorStyles: {
    paddingLeft: '0',
    content: '·'
  },
  data: {}
};

const OfferHorizontalCard = (props) => {
  const {
    imageProps,
    data,
    focusReferences,
    focusable,
    airbnbItemStyles,
    imageSection,
    contentSection,
    featuredOffersListContainer
  } = props;
  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const { handleClick, handleItemKeyDown } = handleFareItemInteraction(props);

  return (
    <Box
      focusable={focusable}
      tabIndex={focusable ? 0 : -1}
      focusReferences={focusReferences}
      onKeyDown={handleItemKeyDown}
      onClick={handleClick}
      className={className}
      style={style}
      dataTestId="offerHorizontalLayoutRow"
      {...featuredOffersListContainer}
    >
      <Box {...imageSection}>
        <ImageHeader imageHeight="100%" {...imageProps} image={data.destinationImage} />
      </Box>
      <Box {...contentSection}>
        <ItemCard {...airbnbItemStyles} data={data} />
      </Box>
    </Box>
  );
};

OfferHorizontalCard.propTypes = {
  /**
   * Class name of header
   */
  className: PropTypes.string,
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * Image of destination.
   */
  image: PropTypes.string,
  /**
   * image props.
   */
  imageProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Name of destination.
   */
  name: PropTypes.string,
  /**
   * iataCode of destination.
   */
  iataCode: PropTypes.string,
  /**
   * list of fares.
   */
  fares: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  /**
   * Header props
   */
  header: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interationHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * Determine if the image will be displayed in the component.
   */
  showImage: PropTypes.bool,
  /**
   * Show or hide the city name
   */
  showCityName: PropTypes.bool,
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * Call to action onClick event
   */
  callToActionOnClick: PropTypes.func,
  /**
   * OnKeyDown function.
   */
  onKeyDown: PropTypes.func,
  /**
   * FocusReferences functions
   */
  focusReferences: PropTypes.func,
  /**
   * Turn off or on tabIndex in the OfferHorizontalCard
   */
  focusable: PropTypes.bool,
  /**
   * Traffix variants
   */
  traffixVariant: PropTypes.string,
  /**
   * Settings for the card
   */
  airbnbItemStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings for the image section of the card
   */
  imageSection: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings for the content section of the card
   */
  contentSection: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings for the featured offers list container
   */
  featuredOffersListContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * data to render by the item
   */
  data: PropTypes.objectOf(PropTypes.any)
};
OfferHorizontalCard.defaultProps = {
  className: '',
  style: null,
  image: '',
  name: '',
  iataCode: '',
  fares: [],
  imageProps: {},
  header: {},
  interationHotZone: 'smart',
  translate: () => {},
  onClick: (props) => props,
  callToActionOnClick: (props) => props,
  showImage: true,
  showCityName: true,
  onKeyDown: () => {},
  focusReferences: () => {},
  focusable: false,
  traffixVariant: 'ci',
  airbnbItemStyles: {},
  featuredOffersListContainer: {
    display: 'flex'
  },
  imageSection: {
    flex: '1'
  },
  contentSection: {
    flex: '2',
    paddingLeft: '2',
    paddingTop: '1'
  },
  data: null
};
OfferHorizontalCard.componentName = 'OfferHorizontalCard';

export default OfferHorizontalCard;
